import { useCallback, useEffect, useRef, useState } from "react";
import { apiService } from "../../services/api";

const buildToken = (headers = {}) => {
  const raw = headers.Authorization || headers.authorization || "";
  return raw.replace(/Bearer\s+/i, "");
};

const useKickoffSession = ({ projectId, user, getAuthHeaders }) => {
  const [kickoffSession, setKickoffSession] = useState(null);
  const [presenceCount, setPresenceCount] = useState(0);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [kickoffError, setKickoffError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const socketRef = useRef(null);
  const rejoinTimeoutRef = useRef(null);
  const clientIdRef = useRef(`kickoff-${Date.now()}-${Math.random().toString(16).slice(2)}`);

  const createOrJoinKickoff = useCallback(async () => {
    if (!projectId) {
      setKickoffSession(null);
      return;
    }
    try {
      setKickoffError(null);
      const created = await apiService.post("/kickoffs", { project_id: projectId });
      setKickoffSession(created || null);
    } catch (err) {
      setKickoffError(err?.message || "Impossible de demarrer le kickoff.");
    }
  }, [projectId]);

  const scheduleRejoin = useCallback(() => {
    if (!projectId) return;
    if (rejoinTimeoutRef.current) return;
    rejoinTimeoutRef.current = setTimeout(() => {
      rejoinTimeoutRef.current = null;
      createOrJoinKickoff();
    }, 250);
  }, [createOrJoinKickoff, projectId]);

  useEffect(() => {
    createOrJoinKickoff();
  }, [createOrJoinKickoff]);

  const sendKickoffRealtime = useCallback((payload) => {
    const ws = socketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  const notifyRefresh = useCallback(
    (scope) => {
      const senderId = user?.id ?? user?.employee_id ?? null;
      return sendKickoffRealtime({
        type: "refresh",
        scope,
        sender_id: senderId,
        sender_client_id: clientIdRef.current,
      });
    },
    [sendKickoffRealtime, user]
  );

  useEffect(() => {
    const kickoffId = kickoffSession?.id;
    const authHeaders = getAuthHeaders ? getAuthHeaders() : apiService.getHeaders();
    const token = buildToken(authHeaders);
    if (!kickoffId || !token) {
      setRealtimeConnected(false);
      setPresenceCount(0);
      setConnectedUsers([]);
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      return;
    }

    const wsBase = apiService.getBaseUrl().replace(/^http/i, "ws");
    const socket = new WebSocket(`${wsBase}/kickoffs/${kickoffId}/ws?token=${encodeURIComponent(token)}`);
    socketRef.current = socket;

    socket.onopen = () => setRealtimeConnected(true);
    socket.onclose = (event) => {
      setRealtimeConnected(false);
      setPresenceCount(0);
      setConnectedUsers([]);
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      const reason = event?.reason || "";
      if (reason.toLowerCase().includes("kickoff")) {
        scheduleRejoin();
      }
    };
    socket.onerror = () => setRealtimeConnected(false);
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "sync_state") {
          if (data.kickoff) setKickoffSession(data.kickoff);
          if (typeof data.connected === "number") setPresenceCount(data.connected);
          if (Array.isArray(data.connected_users)) setConnectedUsers(data.connected_users);
        } else if (data.type === "presence") {
          setPresenceCount(typeof data.connected === "number" ? data.connected : 0);
          if (Array.isArray(data.users)) setConnectedUsers(data.users);
        } else if (data.type === "refresh") {
          const senderClientId = data.sender_client_id;
          if (senderClientId && String(senderClientId) === String(clientIdRef.current)) {
            return;
          }
          setRefreshToken((prev) => prev + 1);
        } else if (data.type === "kickoff_closed") {
          setKickoffSession(null);
          setPresenceCount(0);
          setConnectedUsers([]);
          scheduleRejoin();
        }
      } catch (err) {
        console.error("WebSocket parse error", err);
      }
    };

    return () => {
      try {
        socket.close();
      } catch (err) {
        // ignore
      }
    };
  }, [kickoffSession?.id, getAuthHeaders, user, scheduleRejoin]);

  useEffect(() => {
    return () => {
      if (rejoinTimeoutRef.current) {
        clearTimeout(rejoinTimeoutRef.current);
      }
    };
  }, []);

  return {
    kickoffSession,
    presenceCount,
    connectedUsers,
    realtimeConnected,
    kickoffError,
    refreshToken,
    notifyRefresh,
  };
};

export default useKickoffSession;
